import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../../../api/client.js";

/**
 * FIX-067 Stage B: the daily automation (source intake + one automatic post per channel per day). Owner only — the server enforces the same.
 * Everything starts OFF; each switch below is stored in the database and takes effect on the next minute's check.
 */
interface SourceView {
  readonly username: string;
  readonly role: "primary" | "fallback";
  readonly enabled: boolean;
  readonly lastMessageId: number | null;
  readonly lastReadAt: string | null;
  readonly lastError: string | null;
}
interface RunView {
  readonly id: string;
  readonly localDate: string;
  readonly trigger: string;
  readonly status: string;
  readonly capUsd: number;
  readonly plannedUsd: number;
  readonly error: string | null;
  readonly startedAt: string;
  readonly report: { readonly requests?: readonly unknown[]; readonly skipped?: readonly unknown[]; readonly errors?: readonly string[] };
}
interface ScheduleView {
  readonly timeZone: string;
  readonly intakeAt: string;
  readonly publishAt: string;
  readonly today: string;
  readonly nextIntakeAt: string;
  readonly nextPublishAt: string;
}
interface StatusView {
  readonly schedule?: ScheduleView;
  readonly enabled: boolean;
  readonly autoPublishEnabled: boolean;
  readonly autoPublishAt: string;
  readonly channels: readonly { readonly key: string; readonly title: string; readonly username: string; readonly autoPublishEnabled: boolean }[];
  readonly intake?: { readonly enabled: boolean; readonly at: string; readonly dailyCapUsd: number; readonly plannedUsdToday: number; readonly lastRun: RunView | null; readonly sources: readonly SourceView[] };
}
interface InboxPost {
  readonly id: string;
  readonly channel: string;
  readonly messageId: number;
  readonly url: string;
  readonly kind: string;
  readonly photoCount: number;
  readonly caption: string | null;
  readonly status: string;
  readonly skipReason: string | null;
  readonly requests: readonly InboxRequest[];
}
interface InboxRequest {
  readonly id?: string;
  readonly channelKey: string;
  readonly batchId: string;
  readonly localDate: string;
  readonly clothingColor: string | null;
  readonly contentMode?: string;
  /** Owner t1114u: the caption saved for this request's automatic post. */
  readonly captionText?: string | null;
  /** Owner t1114u: today's requests only — the image(s) the 19:00 post would use (empty = none ready yet). */
  readonly imageUrls?: readonly string[];
}

const CAPTION_LIMIT = 1000;

/** Owner t1114u: one of today's automatic requests — the image, the source caption + link, and the caption box. */
function TodayCaption({ post, request, channelTitle, busy, onSave }: { readonly post: InboxPost; readonly request: InboxRequest; readonly channelTitle: string; readonly busy: boolean; readonly onSave: (text: string) => Promise<void> }) {
  const [draft, setDraft] = useState(request.captionText ?? "");
  useEffect(() => setDraft(request.captionText ?? ""), [request.captionText]);
  const dirty = draft.trim() !== (request.captionText ?? "");
  return (
    <div className="card" data-testid={`today-caption-${request.channelKey}`}>
      <h4>{channelTitle}</h4>
      {(request.imageUrls ?? []).length > 0 ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(request.imageUrls ?? []).map((u) => (
            <img key={u} src={u} alt="Today's image" loading="lazy" style={{ maxWidth: 240 }} />
          ))}
        </div>
      ) : (
        <p className="muted">No image is ready yet (still being made, or no image passed every check).</p>
      )}
      <p>
        Source post: <a href={post.url} target="_blank" rel="noreferrer">@{post.channel}/{post.messageId}</a>
      </p>
      <p className="muted" style={{ whiteSpace: "pre-wrap" }} data-testid={`today-source-caption-${request.channelKey}`}>
        {post.caption ? post.caption : "(the source post has no caption)"}
      </p>
      <label>
        Caption for the automatic post
        <textarea value={draft} maxLength={CAPTION_LIMIT} rows={4} style={{ width: "100%" }} onChange={(e) => setDraft(e.target.value)} data-testid={`today-caption-input-${request.channelKey}`} />
      </label>
      <div className="muted">
        {request.captionText ? "Saved." : "No caption saved yet."}
      </div>
      <button type="button" className="primary" disabled={busy || !dirty || draft.trim().length === 0 || request.id === undefined} data-testid={`today-caption-save-${request.channelKey}`} onClick={() => void onSave(draft)}>
        Save caption
      </button>
    </div>
  );
}

const usd = (n: number) => `$${n.toFixed(2)}`;
/** e.g. "Mon, Nov 2, 10:00 AM" in the given zone. */
export const zoned = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(iso));

export function TelegramAutomationPanel({ tenantId, csrfToken }: { readonly tenantId: string; readonly csrfToken: string | null }) {
  const base = `/images/${encodeURIComponent(tenantId)}`;
  const [status, setStatus] = useState<StatusView | null>(null);
  const [posts, setPosts] = useState<InboxPost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get<StatusView>(`${base}/telegram-publish/status`), api.get<{ posts: InboxPost[] }>(`${base}/telegram-intake/posts?limit=30`)]);
      setStatus(s);
      setPosts(p.posts);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the automation status.");
    }
  }, [base]);
  useEffect(() => {
    void load();
  }, [load]);
  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`${base}${path}`, csrfToken, body);
      await load();
    } catch (e) {
      const v = e instanceof ApiError ? (e.body as { violations?: unknown } | null)?.violations : undefined;
      setError(e instanceof ApiError ? `${e.message}${Array.isArray(v) && v.length > 0 ? ` (${v.join(", ")})` : ""}` : "The action failed.");
    } finally {
      setBusy(false);
    }
  };
  if (status === null) return <div className="card">{error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>}</div>;
  const intake = status.intake;
  const run = intake?.lastRun ?? null;
  return (
    <div className="grid" style={{ gap: 12 }} data-testid="telegram-automation">
      <div className="card">
        <h3>Daily automation</h3>
        {error && <p className="error">{error}</p>}
        <p className="muted">
          New source posts are read once a day at {intake?.at ?? "—"} New York time. One new image per channel is published automatically at {status.autoPublishAt} New York time, only when every check on it passed.
          Times follow US daylight saving automatically.
          Daily spending cap: {intake ? usd(intake.dailyCapUsd) : "—"} (planned today: {intake ? usd(intake.plannedUsdToday) : "—"}).
        </p>
        {status.schedule && (
          <p data-testid="telegram-schedule">
            Next read: {zoned(status.schedule.nextIntakeAt, status.schedule.timeZone)} New York. Next post: {zoned(status.schedule.nextPublishAt, status.schedule.timeZone)} New York.
          </p>
        )}
        {(!status.autoPublishEnabled || intake?.enabled === false) && <p className="error">The automation is switched off in this version.</p>}
        <h4>Automatic posting</h4>
        <table>
          <tbody>
            {status.channels.map((c) => (
              <tr key={c.key}>
                <td>{c.title} (@{c.username})</td>
                <td data-testid={`auto-state-${c.key}`}>{c.autoPublishEnabled ? "On" : "Off"}</td>
                <td>
                  <button type="button" disabled={busy} data-testid={`auto-toggle-${c.key}`} onClick={() => void act(`/telegram-publish/channels/${c.key}/auto`, { enabled: !c.autoPublishEnabled })}>
                    {c.autoPublishEnabled ? "Turn off" : "Turn on"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <h4>Sources</h4>
        <table>
          <tbody>
            {(intake?.sources ?? []).map((s) => (
              <tr key={s.username}>
                <td>
                  <a href={`https://t.me/${s.username}`} target="_blank" rel="noreferrer">@{s.username}</a> ({s.role === "primary" ? "main" : "backup"})
                </td>
                <td data-testid={`source-state-${s.username}`}>{s.enabled ? "On" : "Off"}</td>
                <td className="muted">{s.lastReadAt ? `Last read ${new Date(s.lastReadAt).toLocaleString()}` : "Never read"}{s.lastError ? ` — ${s.lastError}` : ""}</td>
                <td>
                  <button type="button" disabled={busy} data-testid={`source-toggle-${s.username}`} onClick={() => void act(`/telegram-intake/sources/${encodeURIComponent(s.username)}/enabled`, { enabled: !s.enabled })}>
                    {s.enabled ? "Turn off" : "Turn on"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <h4>Last run</h4>
        {run === null ? (
          <p className="muted">No run yet.</p>
        ) : (
          <p data-testid="intake-last-run">
            {run.localDate} · {run.status} · {run.report.requests?.length ?? 0} new request(s) · planned {usd(run.plannedUsd)} of {usd(run.capUsd)}
            {run.error ? ` · ${run.error}` : ""}
          </p>
        )}
        <button type="button" disabled={busy} data-testid="intake-run-now" onClick={() => void act("/telegram-intake/run")}>
          Run now
        </button>
      </div>
      <div className="card" data-testid="today-captions">
        <h3>Today's captions</h3>
        <p className="muted">
          Write the caption for each channel's automatic post of today ({status.schedule?.today ?? "—"}, New York). It is posted with the image at {status.autoPublishAt} New York time. Links, handles and
          promotional text are not allowed. If no caption is saved by then, nothing is posted that day (pending the owner's decision).
        </p>
        {(() => {
          const today = (posts ?? []).flatMap((p) => p.requests.filter((r) => r.localDate === status.schedule?.today).map((r) => ({ post: p, request: r })));
          if (today.length === 0) return <p className="muted">No automatic request today yet.</p>;
          return today.map(({ post, request }) => (
            <TodayCaption
              key={request.id ?? request.batchId}
              post={post}
              request={request}
              channelTitle={status.channels.find((c) => c.key === request.channelKey)?.title ?? request.channelKey}
              busy={busy}
              onSave={(text) => act(`/telegram-intake/requests/${encodeURIComponent(request.id ?? "")}/caption`, { text })}
            />
          ));
        })()}
      </div>
      <div className="card" data-testid="intake-inbox">
        <h3>Source posts</h3>
        {posts === null || posts.length === 0 ? (
          <p className="muted">Nothing read yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Post</th>
                <th>Type</th>
                <th>Status</th>
                <th>Requests</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id}>
                  <td>
                    <a href={p.url} target="_blank" rel="noreferrer">@{p.channel}/{p.messageId}</a>
                  </td>
                  <td>{p.kind === "video" ? "Video (skipped)" : `${p.photoCount} photo(s)`}</td>
                  <td>{p.status}{p.skipReason ? ` (${p.skipReason})` : ""}</td>
                  <td>{p.requests.map((r) => `${r.channelKey}: ${(r.contentMode ?? "nsfw").toUpperCase()}, ${r.clothingColor ?? "—"}`).join("; ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
